import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, filter, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { BasicModalComponent } from 'src/app/modules/shared/components/basic-modal/basic-modal.component';
import {
  NO_BUTTON,
  YES_BUTTON,
} from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import {
  IParentQuestion,
  IQuestionnaire,
} from 'src/app/modules/shared/models/operation.model';
import { SearchService } from 'src/app/pages/search/search.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { AthoraPayloadUtils } from 'src/app/utils/AthoraPayloadUtils';
import { QUESTION_CODES_WITH_ANAGRAFICA_REQUEST } from '../../athora.constants';
import { SubjectPfInsertFormComponent } from '../subject-pf-insert-form/subject-pf-insert-form.component';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrl: './questionnaire.component.scss',
})
export class QuestionnaireComponent implements OnInit, OnDestroy, OnChanges {
  @Input() form: FormGroup;
  @Input() formKeys: IFormKey[] = [];
  @Input() questionnaire: IQuestionnaire = null;
  @Input() newLiquidationSubjects: Record<string, { rawFormOutcome: Record<string, any>; mappedOutcome: Record<string, any> }>;

  @Output() viewSubject = new EventEmitter<IFormKey>();
  @Output() deleteSubject = new EventEmitter<IFormKey>();
  @Output() newLiquidationSubjectsChange = new EventEmitter<Record<string, { rawFormOutcome: Record<string, any>; mappedOutcome: Record<string, any> }>>();

  destroy$ = new Subject();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _spinnerService: SpinnerService,
    private readonly _searchService: SearchService,
    private readonly _alertService: AlertService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['newLiquidationSubjects']) {
      this.formKeys.forEach((key: IFormKey) => {
        if (QUESTION_CODES_WITH_ANAGRAFICA_REQUEST.includes(key.controlName)) {
          const hasDisableProperty = this.newLiquidationSubjects.hasOwnProperty(
            key.controlName
          );
          const inputControl = this.form.get(key.controlName);

          key['disableAnagraficaModalButton'] = !hasDisableProperty;
          hasDisableProperty ? inputControl.disable() : inputControl.enable();
        }
      });
    }
  }

  ngOnInit(): void {
    this.checkDisabledKeys();
    this.watchForm();
    this.addModalButtonToAnagraficaQuestions();
    this.subscribeToLiquidationHoldersInputChange();
  }

  checkDisabledKeys(): void {
    if (this.form && this.formKeys) {
      this.formKeys.forEach((key) => {
        if (key.readonly) {
          this.form.get(key.controlName).disable();
        }
      });
    }
  }

  watchForm() {
    this.form.valueChanges
      .pipe(startWith(this.form.value), takeUntil(this.destroy$))
      .subscribe((value) => {
        LoggerService.logInfo(this.constructor.name, 'watchForm', { value });
        this.checkVisibleQuestions();
      });
  }

  checkVisibleQuestions() {
    this.formKeys.forEach((key) => {
      const question = this.questionnaire.questions.find(
        (x) => x.questionCode === key.controlName
      );
      let check = false;
      if (question && question.parentQuestions?.length > 0) {
        let parentsDisabled = true;
        question.parentQuestions.forEach((parentQuestion: IParentQuestion) => {
          const parentQuestionCode: string = parentQuestion?.code;
          const parentValue = this.form.get(parentQuestionCode).value;
          const answerEnablingValues: string[] =
            parentQuestion.answerEnablingValues;
          const parentKey = this.formKeys.find(
            (x) => x.controlName === parentQuestionCode
          );
          parentsDisabled = parentKey?.hidden && parentsDisabled;
          check =
            !answerEnablingValues.includes(parentValue) || parentsDisabled;
        });
      }
      key.hidden = check;
    });
    LoggerService.logInfo(this.constructor.name, 'checkVisibleQuestions', {
      keys: this.formKeys,
    });
  }

  addModalButtonToAnagraficaQuestions(): void {
    this.formKeys.forEach((key: IFormKey) => {
      if (QUESTION_CODES_WITH_ANAGRAFICA_REQUEST.includes(key.controlName)) {
        key['hasAnagraficaModalButton'] = true;

        // Check if subject already exists (e.g., loaded from draft)
        const subjectExists = this.newLiquidationSubjects?.hasOwnProperty(key.controlName);

        if (!key.hasOwnProperty('disableAnagraficaModalButton')) {
          key['disableAnagraficaModalButton'] = !subjectExists;
        }

        // Enable control only if subject doesn't exist yet
        const control = this.form.get(key.controlName);
        if (subjectExists) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }

  subscribeToLiquidationHoldersInputChange() {
    if (!this.form) { return; }
    QUESTION_CODES_WITH_ANAGRAFICA_REQUEST.forEach((questionCode) => {
      const control = this.form.get(questionCode);
      if (!control) { return; }
      control.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          filter((inputValue: string) =>
            /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(inputValue?.trim())
          ),
          filter(() => {
            // Skip API call if subject already exists in map (loaded from draft)
            return !this.newLiquidationSubjects?.[questionCode];
          }),
          switchMap((inputValue) => {
            // Clear previous state when making a new lookup
            const keyObj = this.formKeys.find((key) => key.controlName === questionCode);
            if (keyObj) {
              (keyObj as any).alreadyExistsInRegistry = false;
              (keyObj as any).disableAnagraficaModalButton = true;
            }

            this._spinnerService.show();
            return this._searchService.getAnagrafica(inputValue).pipe(
              catchError((error) => {
                this._spinnerService.hide();

                if (error.status === 404) {
                  console.warn('CF not found in registry. Opening modal for manual insertion.');
                  // Reset the alreadyExistsInRegistry flag since CF was not found
                  const keyObj = this.formKeys.find((key) => key.controlName === questionCode);
                  if (keyObj) {
                    (keyObj as any).alreadyExistsInRegistry = false;
                  }

                  this._dialog
                    .open(SubjectPfInsertFormComponent, {
                      data: {
                        fiscalCode: this.form.get(questionCode).value,
                      },
                      minWidth: '70vw',
                      minHeight: '55vh',
                      disableClose: true,
                    })
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe((outcome) => {
                      if (outcome?.resetInput) {
                        LoggerService.logInfo(this.constructor.name, 'subscribeToLiquidationHoldersInputChange', { outcome });
                        this.form.get(questionCode).reset();
                        // Reset button states when input is reset
                        if (keyObj) {
                          (keyObj as any).disableAnagraficaModalButton = true;
                        }
                      } else if (outcome) {
                        // Reassign the entire object to trigger Change Detection
                        this.newLiquidationSubjects = {
                          ...this.newLiquidationSubjects,
                          [questionCode]: {
                            rawFormOutcome: outcome,
                            mappedOutcome: {
                              ...AthoraPayloadUtils.mapPhysicalSubject({
                                ...outcome.individual,
                                ...outcome.birth,
                                ...outcome.residence,
                              }),
                              privacy: AthoraPayloadUtils.setSubjectPrivacy(),
                            },
                          },
                        };
                        // Emit to parent so it can update its reference
                        this.newLiquidationSubjectsChange.emit(this.newLiquidationSubjects);
                        // Enable the modal buttons now that subject exists
                        if (keyObj) {
                          (keyObj as any).disableAnagraficaModalButton = false;
                        }
                      } else {
                        // User cancelled modal - keep buttons enabled for retry
                        if (keyObj) {
                          (keyObj as any).disableAnagraficaModalButton = false;
                        }
                      }
                    });
                } else {
                  console.error('Something went wrong when calling Registry API.', error.error);
                  this._alertService.openErrorMessage(
                    'Errore durante la ricerca dell\'anagrafica nel registro.',
                    this.constructor.name,
                    error,
                    false
                  );
                }

                // Return EMPTY so stream continues after error
                return EMPTY;
              })
            );
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            console.log('CF found in registry. Nothing else to do.');
            const keyObj = this.formKeys.find((key) => key.controlName === questionCode);
            if (keyObj) {
              (keyObj as any).alreadyExistsInRegistry = true;
            }
            this._spinnerService.hide();
          },
        });
    });
  }

  openSubjectModal(key: IFormKey) {
    this.viewSubject.emit(key);
  }

  removeSubject(key: IFormKey) {
    this._dialog
      .open(BasicModalComponent, {
        data: {
          title: 'Attenzione',
          text: 'Si sta per cancellare il soggetto inserito. Continuare?',
          modalSize: 'modal-box-small text-center vertical-align',
          buttons: [NO_BUTTON, YES_BUTTON],
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          if (result === YES_BUTTON.output) {
            // Always emit to parent (athora.component handles deletion for RISPA/RISTO)
            this.deleteSubject.emit(key);

            // For SIMOR (modal context), also handle locally to update UI immediately
            // Parent will receive the deletion but won't update our @Input directly
            if (this.newLiquidationSubjects && this.newLiquidationSubjects[key.controlName]) {
              const updated = { ...this.newLiquidationSubjects };
              delete updated[key.controlName];
              this.newLiquidationSubjects = updated;
              this.newLiquidationSubjectsChange.emit(updated);
            }

            // Reset form control and re-enable it
            const control = this.form.get(key.controlName);
            control.reset();
            control.enable();

            // Update button state
            key['disableAnagraficaModalButton'] = true;
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
